import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { MessageDetailsModalComponent } from '../../components/message-details-modal/message-details-modal.component';
import { LockdownMessage, MESSAGE_TYPE_LABELS } from '../../../shared/models/message.model';
import { Icons } from '../../../shared/helpers/icons';
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { LockdownUserService } from '../../../shared/services/lockdown-user.service';

type SortableColumn = 'subject' | 'sender' | 'type' | 'created';

@Component({
  selector: 'app-admin-messages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    NgxPaginationModule,
    SpinnerComponent,
    MessageDetailsModalComponent
  ],
  templateUrl: './admin-messages.component.html',
  styleUrl: './admin-messages.component.css'
})
export class AdminMessagesComponent implements OnInit, OnDestroy {
  icons = Icons;
  readonly messageTypeOptions = Object.entries(MESSAGE_TYPE_LABELS)
    .map(([value, label]) => ({ value: Number(value), label }));

  messages:         LockdownMessage[] = [];
  filteredMessages: LockdownMessage[] = [];
  searchQuery = '';
  filterType: number | '' = '';
  filterClientId = '';
  clientOptions: { id: string; name: string }[] = [];
  page = 1;
  readonly itemsPerPage = 10;

  sortColumn: SortableColumn | null = 'created';
  sortDirection: 'asc' | 'desc' = 'desc';

  loading = false;

  tooltipX = 0;
  tooltipY = 0;

  selectedMessage: LockdownMessage | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    protected userDataService: UserDataService,
    private lockdownUserService: LockdownUserService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const buildingId = this.userDataService.buildingId();
    if (!buildingId) return;

    this.loading = true;
    this.lockdownUserService.getMessages(buildingId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: messages => {
          this.messages = messages;
          this.loading  = false;
          this.filterMessages();
        },
        error: (err: Error) => {
          this.loading = false;
          this.toastr.error(err.message, 'Failed to load messages');
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sortBy(column: SortableColumn): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn    = column;
      this.sortDirection = 'asc';
    }
    this.filterMessages();
  }

  trackByClientId(_: number, c: { id: string; name: string }): string {
    return c.id;
  }

  onClientFilterChange(): void {
    this.filterMessages();
  }

  filterMessages(): void {
    const q    = this.searchQuery.toLowerCase();
    const type = this.filterType;

    const preFiltered = this.messages.filter(m => {
      if (type !== '' && m.type !== type) return false;
      return (
        (m.subject ?? '').toLowerCase().includes(q) ||
        (m.body ?? '').toLowerCase().includes(q) ||
        (m.senderName ?? '').toLowerCase().includes(q) ||
        (m.senderEmail ?? '').toLowerCase().includes(q) ||
        m.sentTo.some(r => (r ?? '').toLowerCase().includes(q))
      );
    });

    const seen = new Set<number>();
    this.clientOptions = [];
    for (const m of this.messages) {
      if (!seen.has(m.clientId)) {
        seen.add(m.clientId);
        this.clientOptions.push({
          id:   String(m.clientId),
          name: m.clientName ?? `Building ${m.clientId}`
        });
      }
    }

    if (this.userDataService.isOrg()) {
      const currentId = this.userDataService.buildingId() ?? '';
      if (currentId && !this.clientOptions.some(c => c.id === currentId)) {
        this.clientOptions.unshift({ id: currentId, name: 'Current Building' });
      }
      if (!this.filterClientId) {
        this.filterClientId = this.clientOptions.some(c => c.id === currentId)
          ? currentId
          : (this.clientOptions[0]?.id ?? '');
      }
      if (this.filterClientId && !this.clientOptions.some(c => c.id === this.filterClientId)) {
        this.filterClientId = currentId || (this.clientOptions[0]?.id ?? '');
      }
    }

    this.filteredMessages = this.filterClientId
      ? preFiltered.filter(m => String(m.clientId) === this.filterClientId)
      : preFiltered;

    if (this.sortColumn) {
      const col = this.sortColumn;
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      this.filteredMessages.sort((a, b) => {
        if (col === 'type') return (a.type - b.type) * dir;
        const aVal = col === 'subject' ? (a.subject ?? '') : col === 'sender' ? a.senderName : (a.created ?? '');
        const bVal = col === 'subject' ? (b.subject ?? '') : col === 'sender' ? b.senderName : (b.created ?? '');
        return aVal.localeCompare(bVal) * dir;
      });
    }

    this.page = 1;
  }

  messageTypeLabel(type: number): string {
    return MESSAGE_TYPE_LABELS[type] ?? 'Unknown';
  }

  onRowMouseMove(event: MouseEvent): void {
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY;
  }

  openDetailsModal(message: LockdownMessage): void {
    this.selectedMessage = message;
  }

  closeDetailsModal(): void {
    this.selectedMessage = null;
  }

  messageTypeBadgeClass(type: number): string {
    switch (type) {
      case 2: return 'bg-orange-100 text-nside-orange-400';  // Admin Users
      case 3: return 'bg-blue-50 text-blue-600';             // All Lockdown Users
      case 4: return 'bg-purple-50 text-purple-600';         // All App Users
      case 5: return 'bg-teal-50 text-teal-600';             // Admin Message V1
      default: return 'bg-gray-100 text-gray-500';           // Test (Self Only)
    }
  }
}
